import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";

import Contexts from './Contexts';
import ErrorBoundary from './ErrorBoundary';
import Eula from './Eula';
import FeedbackForm from './FeedbackForm';
import Footer from './Footer';
import Header from './homepage/Header';
import Help from './Help';
import HelpForPractitioner from './HelpForPractitioner';
import HelpGuidance from './HelpGuidance';
import HelpIntro from './HelpIntro';
import HelpSE from './HelpSE';
import HelpTips from './HelpTips';
import Loading from './Loading';
import LoadingIndicator from './LoadingIndicator';
import Logout from './Logout';
import Privacy from './Privacy';
import ScrollToTop from './ScrollToTop';
import ThankYou from './ThankYou';
import TimeoutLogout from './TimeoutLogout';
import { server } from './actions/fetch';

export const demoOnly = false;

const LandingPageRoute = lazy(() => import('./LandingPageRoute').catch(console.error));
const SignedInRoute = lazy(() => import('./SignedInRoute').catch(console.error));
const DemoRoute = lazy(() => import('./DemoRoute').catch(console.error));

const base = process.env.PUBLIC_URL;
const supportsHistory = 'pushState' in window.history;

export function RedirectExternal({ to }) {
  if (navigator.userAgent !== 'ReactSnap') {
    document.location.replace(to.pathname || to);
  }
  return null;
}

export const dashboardUrls = Object.freeze([
  '/agp/:days',
  '/insulin/:days',
  '/nutrition/:days',
  '/exercise/:days',
  '/day/:days',
  '/diary/:days',
]);

const urlsWithDayParam = [
  ...dashboardUrls,
  ...dashboardUrls.map(u => `/reports${u}`),
];

export const accountUrls = Object.freeze([
  '/account',
  '/datasources', '/sources',
  '/consent', '/consents', '/sharing',
  '/export',
  '/settings',
  '/logout',
]);

export function getView(path) {
  // \/[a-z]+\/(?![a-z]+\/) matches the last instance of /abc/ kind of string.
  const match = path.match(/\/[a-z]+\/(?![a-z]+\/)/g);
  return match ? match[0].replace(/\//g, '') : undefined;
}

function stripParams(path) {
  return path.substring(0, path.indexOf('/', 2) + 1);
}

const protectedUrls = [...dashboardUrls.map(stripParams), ...accountUrls, '/permissions'];

function Routes() {
  return (
    <ErrorBoundary>
      <Router basename={base} forceRefresh={!supportsHistory}>
        <Suspense fallback={<Loading />}>
          <Route path={[...urlsWithDayParam, '/*', ]}>
            <Contexts>
              <Switch>
                {/* Removes trailing slashes */}
                <Route
                  path="/:url*(/+)"
                  exact
                  strict
                  render={({ location }) => (
                    <Redirect to={location.pathname.replace(/\/+$/, "")} />
                  )}
                />
                {/* Removes duplicate slashes in the middle of the URL */}
                <Route
                  path="/:url(.*//+.*)"
                  exact
                  strict
                  render={({ match }) => (
                    <Redirect to={`/${match.params.url.replace(/\/\/+/, "/")}`} />
                  )}
                />
                <Route path={['/logout/:then', '/logout']} component={Logout} />
                <Route path="/connect" children={() => {
                  const { href, search = '' } = document.location;
                  const redirect = search.match(/(\?r=|&r=|\?redirect=|&redirect=)([^&]+)/)
                    ? ''
                    : `${search.length ? '&' : '?'}redirect=${
                      encodeURIComponent(href.replace('/connect', '/permissions'))
                    }`
                  return (
                    <RedirectExternal to={`${server}/signin/welcome${search}${redirect}`} />
                  );
                }} />
                <Route path="/signin" children={({ history }) => {
                  const { protocol, host } = document.location;
                  const redirect = history.location.search
                    ? history.location.search.substring('?redirect='.length)
                    : encodeURIComponent(`${protocol}//${host}${process.env.PUBLIC_URL}/agp/`);
                  return (
                    <RedirectExternal to={`${server}/signin/?redirect=${redirect}`} />
                  );
                }} />
                <Route path={[
                  '/eula',
                  '/feedback',
                  '/help',
                  '/helpForPractitioner',
                  '/helpGuidance',
                  '/helpIntro',
                  '/helpSE',
                  '/helpTips',  
                  '/privacy',
                  '/thankyou',
                  '/timeout',
                ]}>
                  <Header interactive={true} />
                  <Switch>
                    <Route path="/eula" component={Eula} />
                    <Route path="/feedback" component={FeedbackForm} />
                    <Route path="/help" component={Help} />
                    <Route path="/helpForPractitioner" component={HelpForPractitioner} />
                    <Route path="/helpGuidance" component={HelpGuidance} />
                    <Route path="/helpIntro" component={HelpIntro} />
                    <Route path="/helpSE" component={HelpSE} />
                    <Route path="/helpTips" component={HelpTips} />
                    <Route path="/privacy" component={Privacy} />
                    <Route path="/thankyou" component={ThankYou} />
                    <Route path="/timeout" component={TimeoutLogout} />
                  </Switch>
                  <ScrollToTop />
                </Route>
                <Route path={protectedUrls} component={SignedInRoute} />
                <Route path="/demo/*" component={DemoRoute} />
                <Route path="/">
                  <LandingPageRoute standalone={demoOnly} />
                </Route>
                <Redirect to="/" />
              </Switch>
              <LoadingIndicator />              
            </Contexts>
          </Route>
          <Footer />
        </Suspense>
      </Router >
    </ErrorBoundary>
  );
}

export default Routes;
